import React, { useState, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Database, Download, Trash2, RotateCcw,
  AlertTriangle, X, CheckCircle, Clock,
  Settings, ShieldCheck, History, Laptop
} from "lucide-react";
import { NotificationContext } from "../../context/NotificationContext";
import authAxios from "../../api/authAxios";

export default function DataControls() {
  const { addNotification } = useContext(NotificationContext);
  const [loading, setLoading] = useState(false);
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [systemSettings, setSystemSettings] = useState({
    auto_backup_enabled: true,
    backup_frequency: "DAILY"
  });

  const [stats, setStats] = useState({
    database_type: "Loading...",
    database_size: "Loading...",
    last_backup: "Loading...",
    backup_history: [],
    system_status: "Checking..."
  });

  useEffect(() => {
    fetchStats();
    fetchSystemSettings();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await authAxios.get('/api/super-admin/data-stats/');
      setStats(res.data);
    } catch (error) {
      console.error("Error fetching data stats:", error);
    }
  };

  const fetchSystemSettings = async () => {
    try {
      const res = await authAxios.get('/api/super-admin/settings/');
      setSystemSettings({
        auto_backup_enabled: res.data.auto_backup_enabled,
        backup_frequency: res.data.backup_frequency
      });
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const updateAutoBackup = async (updates) => {
    try {
      const newSettings = { ...systemSettings, ...updates };
      setSystemSettings(newSettings);
      await authAxios.patch('/api/super-admin/settings/', updates);
      addNotification("Backup settings updated", "success");
    } catch (error) {
      addNotification("Failed to update settings", "error");
    }
  };

  const handleBackupData = async () => {
    try {
      setLoading(true);
      const res = await authAxios.get('/api/super-admin/backup/', { responseType: 'blob' });

      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `backup_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      addNotification("Backup generated and downloaded successfully", "success");
      fetchStats();
    } catch (error) {
      addNotification("Error creating backup", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCleanupData = async () => {
    if (confirmText !== "CONFIRM") return;

    try {
      setLoading(true);
      const res = await authAxios.post('/api/super-admin/cleanup/');
      addNotification(res.data.message || "Cleanup completed successfully", "success");
      setShowCleanupModal(false);
      setConfirmText("");
      fetchStats();
    } catch (error) {
      addNotification("Error during cleanup", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8 pb-12"
    >
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">Data Controls</h2>
          <p className="text-slate-500 dark:text-slate-400 font-medium italic">Manage, secure and optimize platform data</p>
        </div>
        <div className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-500" />
          <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">Enterprise Secured</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Actions */}
        <div className="lg:col-span-2 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Backup Card */}
            <div className="p-8 bg-white dark:bg-slate-800/40 rounded-[32px] border border-slate-200 dark:border-slate-700 shadow-2xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform duration-500">
                <Download size={80} className="text-blue-500" />
              </div>

              <div className="relative z-10">
                <div className="w-12 h-12 bg-blue-500/10 rounded-2xl flex items-center justify-center text-blue-500 mb-6">
                  <Download size={24} />
                </div>
                <h3 className="text-xl font-black text-slate-900 dark:text-white mb-2">On-Demand Backup</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-8 leading-relaxed">Download a point-in-time JSON snapshot containing all system data, users, and transactions.</p>

                <button
                  onClick={handleBackupData}
                  disabled={loading}
                  className="w-full py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black uppercase tracking-widest text-xs transition-all active:scale-95 disabled:opacity-50 shadow-lg shadow-blue-600/20"
                >
                  {loading ? 'Generating...' : 'Start Backup Download'}
                </button>
              </div>
            </div>

            {/* Cleanup Card */}
            <div className="p-8 bg-white dark:bg-slate-800/40 rounded-[32px] border border-slate-200 dark:border-slate-700 shadow-2xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform duration-500">
                <RotateCcw size={80} className="text-orange-500" />
              </div>

              <div className="relative z-10">
                <div className="w-12 h-12 bg-orange-500/10 rounded-2xl flex items-center justify-center text-orange-500 mb-6">
                  <Trash2 size={24} />
                </div>
                <h3 className="text-xl font-black text-slate-900 dark:text-white mb-2">System Cleanup</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-8 leading-relaxed">Optimize database by archiving expired subscriptions and soft-deleting inactive trial accounts.</p>

                <button
                  onClick={() => setShowCleanupModal(true)}
                  disabled={loading}
                  className="w-full py-4 bg-orange-600 hover:bg-orange-500 text-white rounded-2xl font-black uppercase tracking-widest text-xs transition-all active:scale-95 disabled:opacity-50 shadow-lg shadow-orange-600/20"
                >
                  Run Optimization
                </button>
              </div>
            </div>
          </div>

          {/* Backup History */}
          <div className="p-8 bg-white dark:bg-slate-800/40 rounded-[32px] border border-slate-200 dark:border-slate-700 shadow-2xl">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-2xl">
                <History size={20} className="text-slate-500" />
              </div>
              <div>
                <h3 className="text-xl font-black text-slate-900 dark:text-white">Backup History</h3>
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Recent activity and status</p>
              </div>
            </div>

            <div className="space-y-4">
              {stats.backup_history?.length > 0 ? (
                stats.backup_history.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700/50">
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-white dark:bg-slate-700 rounded-xl shadow-sm">
                        <Database size={16} className="text-blue-500" />
                      </div>
                      <div>
                        <p className="text-sm font-black text-slate-900 dark:text-white">
                          {new Date(item.date).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <p className="text-[10px] font-bold text-slate-500 uppercase">{item.type} Backup</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1 bg-emerald-500/10 rounded-full">
                      <CheckCircle size={12} className="text-emerald-500" />
                      <span className="text-[10px] font-black text-emerald-500 uppercase">{item.status}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-12 text-center text-slate-400">
                  <Clock size={40} className="mx-auto mb-4 opacity-20" />
                  <p className="text-sm font-bold uppercase tracking-widest">No history available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Settings & Info */}
        <div className="space-y-8">
          {/* Settings Card */}
          <div className="p-8 bg-white dark:bg-slate-800/40 rounded-[32px] border border-slate-200 dark:border-slate-700 shadow-2xl">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-3 bg-purple-500/10 rounded-2xl text-purple-600">
                <Settings size={20} />
              </div>
              <h3 className="text-xl font-black text-slate-900 dark:text-white">Automation</h3>
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-black text-slate-900 dark:text-white">Auto Backups</p>
                  <p className="text-[10px] font-bold text-slate-500">Cloud scheduled jobs</p>
                </div>
                <button
                  onClick={() => updateAutoBackup({ auto_backup_enabled: !systemSettings.auto_backup_enabled })}
                  className={`w-12 h-6 rounded-full p-1 transition-colors duration-300 ${systemSettings.auto_backup_enabled ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-700'}`}
                >
                  <div className={`w-4 h-4 bg-white rounded-full transition-transform duration-300 ${systemSettings.auto_backup_enabled ? 'translate-x-6' : 'translate-x-0'}`} />
                </button>
              </div>

              <div>
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3">Frequency</p>
                <div className="grid grid-cols-3 gap-2">
                  {['DAILY', 'WEEKLY', 'MONTHLY'].map(freq => (
                    <button
                      key={freq}
                      onClick={() => updateAutoBackup({ backup_frequency: freq })}
                      className={`py-2 rounded-xl text-[10px] font-black transition-all ${systemSettings.backup_frequency === freq
                        ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200'}`}
                    >
                      {freq}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* DB Info Card */}
          <div className="p-8 bg-slate-900 rounded-[32px] border border-slate-800 shadow-2xl text-white overflow-hidden relative">
            <div className="absolute top-0 right-0 p-8 opacity-5">
              <Laptop size={120} />
            </div>
            <div className="relative z-10">
              <h3 className="text-xl font-black mb-6">Environment</h3>
              <div className="space-y-6">
                <div className="flex justify-between items-center py-3 border-b border-white/5">
                  <span className="text-xs font-bold text-slate-400">DB Engine</span>
                  <span className="text-sm font-black text-blue-400">{stats.database_type}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/5">
                  <span className="text-xs font-bold text-slate-400">Total Size</span>
                  <span className="text-sm font-black text-emerald-400">{stats.database_size}</span>
                </div>
                <div className="flex flex-col gap-1 py-3">
                  <span className="text-xs font-bold text-slate-400">Last Sync</span>
                  <span className="text-sm font-black">{stats.last_backup !== "Never" ? new Date(stats.last_backup).toLocaleDateString() : "Never"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <AnimatePresence>
        {showCleanupModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowCleanupModal(false)}
              className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-[32px] shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden"
            >
              <div className="p-8">
                <div className="flex justify-between items-center mb-6">
                  <div className="w-12 h-12 bg-red-500/10 rounded-2xl flex items-center justify-center text-red-500">
                    <AlertTriangle size={24} />
                  </div>
                  <button
                    onClick={() => setShowCleanupModal(false)}
                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                  >
                    <X size={20} className="text-slate-400" />
                  </button>
                </div>

                <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-2 tracking-tight">Destructive Action</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 leading-relaxed">
                  Cleanup will archive expired records and inactive trial accounts. While data persists in archives, this action cannot be easily undone.
                </p>

                <div className="space-y-4">
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 text-center">Type CONFIRM below to verify</p>
                    <input
                      type="text"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                      placeholder="CONFIRM"
                      className="w-full py-4 px-6 bg-slate-100 dark:bg-slate-800 border-none rounded-2xl text-center font-black text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600 focus:ring-2 focus:ring-red-500/50 transition-all uppercase"
                    />
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={() => setShowCleanupModal(false)}
                      className="flex-1 py-4 text-xs font-black uppercase tracking-widest text-slate-600 dark:text-slate-400"
                    >
                      Cancel
                    </button>
                    <button
                      disabled={confirmText !== "CONFIRM" || loading}
                      onClick={handleCleanupData}
                      className="flex-1 py-4 bg-red-600 hover:bg-red-500 text-white rounded-2xl font-black uppercase tracking-widest text-xs transition-all active:scale-95 disabled:opacity-30 shadow-lg shadow-red-600/20"
                    >
                      {loading ? 'Processing...' : 'Execute Cleanup'}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
